/**
 * DescriptionHelpers - types and schemas for component and actor descriptions.
 */

import * as THREE from 'three';
import { z } from 'zod';


const PositionSchema = z.string().describe('A 3D position represented as three numbers (x, y, z)');
type TPosition = z.infer<typeof PositionSchema>;

const RotationSchema = z.string().describe('A 3D rotation represented as three numbers in radian (pitch, yaw, roll)');
type TRotation = z.infer<typeof RotationSchema>;

const ScaleSchema = z.string().describe('A 3D scale represented as three numbers (x, y, z)');
type TScale = z.infer<typeof ScaleSchema>;

const BoundingBoxSchema = z.object({
  // min: PositionSchema.describe('The minimum point of the bounding box, (x, y, z)'),
  // max: PositionSchema.describe('The maximum point of the bounding box, (x, y, z)'),
  center: PositionSchema.describe('The center point of the bounding box, in the form of (x, y, z)'),
  size: ScaleSchema.describe('The size of the bounding box alongside 3 axes, generally referred as width, height and depth, in the form of (x, y, z)'),
}).describe('A bounding box defined by minimum and maximum points in 3D space, if the box is empty, all values are set to zero.');

type TBoundingBox = z.infer<typeof BoundingBoxSchema>;


export {
  BoundingBoxSchema,
  PositionSchema,
  RotationSchema,
  ScaleSchema,

  type TBoundingBox,
  type TPosition,
  type TRotation,
  type TScale
};

function toString(numberArray: number[]): string {
  const str = numberArray.map(num => parseFloat(num.toFixed(5)).toString()).join(', ');
  return `(${str})`;
}

export class DescriptionHelper {
  /**
   * Converts a THREE.Box3 to a BoundingBox object.
   * If the box is empty, returns a bounding box with all values set to zero.
   *
   * @param box - The THREE.Box3 to convert
   * @returns A BoundingBox object
   */
  static dumpBoundingBox(box: THREE.Box3): TBoundingBox {
    if (box.isEmpty()) {
      return {
        center: toString([0, 0, 0]),
        size: toString([0, 0, 0])
      };
    }

    const center = box.getCenter(new THREE.Vector3());
    const size = box.getSize(new THREE.Vector3());
    return {
      // min: toString([box.min.x, box.min.y, box.min.z]),
      // max: toString([box.max.x, box.max.y, box.max.z]),
      center: toString([center.x, center.y, center.z]),
      size: toString([size.x, size.y, size.z])
    };
  }

  /** Dumps a THREE.Vector3 position into a tuple.
   *
   * @param position - The position to convert
   */
  static dumpPosition(position: THREE.Vector3): TPosition {
    return toString([position.x, position.y, position.z]);
  }

  /** Dumps a THREE.Euler rotation into a tuple.
   *
   * @param rotation - The rotation to convert
   */
  static dumpRotation(rotation: THREE.Euler): TRotation {
    return toString([rotation.x, rotation.y, rotation.z]);
  }

  /** Dumps a THREE.Vector3 scale into a tuple.
   *
   * @param scale - The scale to convert
   */
  static dumpScale(scale: THREE.Vector3): TScale {
    return toString([scale.x, scale.y, scale.z]);
  }
}